// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { reducers } from "@insite/client-framework/Store/Reducers";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import { Calculator, Control } from "@stoneage/App/Services/JettingApp/JettingAppService";
import { Draft } from "immer";

export interface xJettingAppCalculatorsState {
    calculators: Calculator[];
    isLoading: boolean;
    failedMessage?: string;
    product?: { [erp: string]: ProductModel };
    isLoadingProduct?: boolean;
    calculatorId?: string;
    graphColors?: { name: string; description: string }[];
    isLoadingSystemList?: boolean;
    acceptConfigButtonExists?: boolean;
    acceptConfigControl?: Control;
    hasVerifySetupButton: boolean;
    hideOtherPanels: boolean;
    verifySetupControl?: Control;
}

const initialState: xJettingAppCalculatorsState = {
    calculators: [],
    isLoading: false,
    failedMessage: "",
    isLoadingProduct: false,
    calculatorId: "",
    graphColors: [],
    isLoadingSystemList: false,
    acceptConfigButtonExists: false,
    acceptConfigControl: undefined,
    verifySetupControl: undefined,
    hasVerifySetupButton: false,
    hideOtherPanels: true,
};

const reducer = {
    "xJettingAppCalculators/BeginLoadJettingAppCalculators": (draft: Draft<xJettingAppCalculatorsState>) => {
        draft.isLoading = true;
    },
    "xJettingAppCalculators/FailedToLoadJettingAppCalculators": (
        draft: Draft<xJettingAppCalculatorsState>,
        action: { failedMessage: string },
    ) => {
        draft.isLoading = false;
        draft.failedMessage = action.failedMessage;
    },
    "xJettingAppCalculators/CompleteLoadJettingAppCalculators": (
        draft: Draft<xJettingAppCalculatorsState>,
        action: { data: Calculator[] },
    ) => {
        draft.isLoading = false;
        draft.failedMessage = "";
        draft.calculators = action.data;
    },
    "xJettingAppCalculators/isProductLoading": (draft: Draft<xJettingAppCalculatorsState>) => {
        draft.isLoadingProduct = true;
    },
    "xJettingAppCalculators/CompleteLoadProduct": (
        draft: Draft<xJettingAppCalculatorsState>,
        action: { product: ProductModel },
    ) => {
        draft.isLoadingProduct = false;
        if (action.product?.productNumber) {
            if (draft.product?.[action.product.productNumber]) {
                draft.product[action.product.productNumber] = action.product;
                return;
            }
            if (!draft.product) {
                draft.product = {};
            }
            draft.product[action.product.productNumber] = action.product;
        }
    },
    "xJettingAppCalculators/UpdateCalculatorId": (
        draft: Draft<xJettingAppCalculatorsState>,
        action: { calculatorId: string },
    ) => {
        draft.calculatorId = action.calculatorId;
    },
    "xJettingAppCalculators/SetGraphColors": (
        draft: Draft<xJettingAppCalculatorsState>,
        action: { colors: { name: string; description: string }[] },
    ) => {
        draft.graphColors = action.colors;
        draft.isLoadingSystemList = false;
    },
    "xJettingAppCalculators/UpdateAcceptConfigButtonExists": (
        draft: Draft<xJettingAppCalculatorsState>,
        action: { acceptConfigButtonExists: boolean; acceptConfigControl: Control },
    ) => {
        if (draft.acceptConfigControl !== action.acceptConfigControl) {
            draft.acceptConfigButtonExists = action.acceptConfigButtonExists;
            draft.acceptConfigControl = action.acceptConfigControl;
        }
    },
    "xJettingAppCalculators/ClearStoredConfigs": (draft: Draft<xJettingAppCalculatorsState>) => {
        draft.acceptConfigButtonExists = false;
        draft.acceptConfigControl = {} as Control;
        draft.verifySetupControl = {} as Control;
        draft.hasVerifySetupButton = false;
    },
    "xJettingAppCalculators/SetIsLoadingSystemList": (draft: Draft<xJettingAppCalculatorsState>) => {
        draft.isLoadingSystemList = true;
    },
    "xJettingAppCalculators/HideOtherPanels": (
        draft: Draft<xJettingAppCalculatorsState>,
        action: { hideOtherPanels: boolean },
    ) => {
        draft.hideOtherPanels = action.hideOtherPanels;
    },
    "xJettingAppCalculators/hasVerifySetupButton": (
        draft: Draft<xJettingAppCalculatorsState>,
        action: { hasVerifySetupButton: boolean; verifySetupControl: Control },
    ) => {
        draft.hasVerifySetupButton = action.hasVerifySetupButton;
        draft.verifySetupControl = action.verifySetupControl;
    },
};

const xJettingAppCalculators = createTypedReducerWithImmer(initialState, reducer);
(reducers as any).xJettingAppCalculators = xJettingAppCalculators;

export type xJettingAppCalculators = {
    xJettingAppCalculators: xJettingAppCalculatorsState;
} & ApplicationState;

type xJettingAppCalculatorsReducers = {
    [key: string]: typeof xJettingAppCalculators;
};

export type xJettingAppCalculatorsActions = Parameters<
    xJettingAppCalculatorsReducers[keyof xJettingAppCalculatorsReducers]
>[1];
